import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { storeApi } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';

interface StoreInfo {
  id: string;
  name: string;
  type: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  coverImage?: string;
  operatingHours?: any;
  dailyCapacity?: number;
  monthlyCapacity?: number;
  slotInterval?: number;
  // LINE 推播
  lineChannelId?: string;
  lineChannelSecret?: string;
  lineAccessToken?: string;
  lineEnabled?: boolean;
  // LINE Login
  lineLoginChannelId?: string;
  lineLoginChannelSecret?: string;
  lineLiffId?: string;
  lineLoginEnabled?: boolean;
}

interface StoreInfoState {
  storeInfo: StoreInfo | null;
  isLoading: boolean;
  error: string | null;

  fetchMyStore: () => Promise<void>; // 新增：管理後台獲取當前認證用戶的店鋪
  fetchStoreInfo: (storeId: string) => Promise<void>; // 客戶端用：根據 storeId 獲取
  updateStoreInfo: (storeId: string, data: Partial<StoreInfo>) => Promise<void>;
}

export const useStoreInfoStore = create<StoreInfoState>()(
  persist(
    (set, get) => ({
      storeInfo: null,
      isLoading: false,
      error: null,

      // 管理後台用：獲取當前認證用戶的店鋪（GET /api/stores）
      fetchMyStore: async () => {
        const { storeInfo } = get();
        const hasExistingData = !!storeInfo;

        // 如果有快取資料，不顯示骨架載入
        set({ isLoading: !hasExistingData, error: null });

        try {
          const response = await storeApi.get(); // 無需 storeId，後端從認證獲取
          set({ storeInfo: response.data, isLoading: false });
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.fetchFailed');
          set({ error: errorMsg, isLoading: false });
          toast.error(errorMsg);
        }
      },

      // 客戶端用：根據 storeId 獲取店鋪公開資訊（GET /api/stores/:storeId）
      fetchStoreInfo: async (storeId: string) => {
        const { storeInfo } = get();
        const hasExistingData = !!storeInfo;

        // 如果有快取資料，不顯示骨架載入
        set({ isLoading: !hasExistingData, error: null });

        try {
          const response = await storeApi.getById(storeId);
          set({ storeInfo: response.data, isLoading: false });
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.fetchFailed');
          set({ error: errorMsg, isLoading: false });
          toast.error(errorMsg);
        }
      },

      updateStoreInfo: async (storeId: string, data: Partial<StoreInfo>) => {
        set({ error: null });

        try {
          await storeApi.update(storeId, data);

          // 樂觀更新本地資料
          set((state) => ({
            storeInfo: state.storeInfo ? { ...state.storeInfo, ...data } : null,
          }));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
          set({ error: errorMsg });
          throw error;
        }
      },
    }),
    {
      name: 'booking-info-storage',
      partialize: (state) => ({
        storeInfo: state.storeInfo,
      }),
    }
  )
);
